import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import { SplitLayoutPlayer } from '../components/remotion';
import { config } from '../config';

const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" width="20" height="20">
    <path fill="#f3f3f3" d="M0 0h23v23H0z" />
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

const Login = () => {
  const { isAuthenticated, login, loginWithCredentials } = useAuth();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showJwtLogin, setShowJwtLogin] = useState(false);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (errorParam) {
      console.error('Login error:', errorParam);
    }
  }, [errorParam]);

  // Load available tenants
  useEffect(() => {
    fetch(`${config.BACKEND_URL}/api/auth/tenants`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTenants(data);
        }
      })
      .catch(err => console.error('Failed to load tenants:', err));
  }, []);

  const handleJwtLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setLoginError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const success = await loginWithCredentials(email, password);
      if (!success) {
        setLoginError('Invalid credentials');
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setLoginError(err.response.data.error);
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="login-split-container">
      {/* Left Panel: Remotion Animation */}
      <div className="login-left-panel">
        <SplitLayoutPlayer className="remotion-player-full" />
      </div>

      {/* Right Panel: Login Form */}
      <div className="login-right-panel">
        <div className="login-form-wrapper">
          <div className="login-header">
            <div className="logo-container">
              <img
                src="https://i.ibb.co/DfXdPcVd/Quanterm-Official-Logo-High-Reso-01.png"
                alt="Quanterm Logo"
                className="quanterm-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="logo-fallback" style={{ display: 'none' }}>
                <div className="fallback-icon">🏢</div>
                <span className="fallback-text">Quanterm</span>
              </div>
            </div>

            <h2 className="login-heading">Welcome to Quanterm Vietnam</h2>
            <p className="login-subheading">Please sign in to continue</p>
          </div>

          {(errorParam || loginError) && (
            <div className="alert alert-error">
              {loginError || (
                errorParam === 'auth_failed' ? 'Authentication failed. Please try again.' :
                  errorParam === 'server_error' ? 'Server error. Please try again later.' :
                    errorParam === 'access_denied' ? 'Access denied. You are not in any allowed Microsoft groups. Please contact your administrator.' :
                      errorParam === 'no_role' ? 'No role assigned. Please contact your administrator to configure group permissions.' :
                        'An error occurred during login.'
              )}
            </div>
          )}

          <div className="login-form">
            {/* Microsoft Login Buttons - one per tenant */}
            {tenants.map((tenant, index) => (
              <button
                key={tenant.key}
                className={`microsoft-login-btn${index > 0 ? ' microsoft-login-btn-alt' : ''}`}
                onClick={() => login(tenant.key)}
                type="button"
                style={index > 0 ? { marginTop: '12px' } : undefined}
              >
                <div className="btn-icon">
                  <MicrosoftIcon />
                </div>
                <span className="btn-text">Sign in with {tenant.label}</span>
              </button>
            ))}

            {/* Fallback if tenants not loaded yet */}
            {tenants.length === 0 && (
              <button
                className="microsoft-login-btn"
                onClick={() => login()}
                type="button"
              >
                <div className="btn-icon">
                  <MicrosoftIcon />
                </div>
                <span className="btn-text">Sign in with Microsoft</span>
              </button>
            )}

            {/* JWT Login - Collapsible */}
            <div className="auth-divider" style={{ marginTop: '24px' }}>
              <span
                onClick={() => setShowJwtLogin(!showJwtLogin)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {showJwtLogin ? 'Hide' : 'Other sign in options'}
              </span>
            </div>

            {showJwtLogin && (
              <form onSubmit={handleJwtLogin} className="jwt-login-form" style={{ marginTop: '16px' }}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="login-btn primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            )}

            <div className="login-footer">
              <div className="security-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Secure Authentication
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
